import {
  Accordion,
  AccordionItem,
  DataTableSkeleton,
  IconButton,
  InlineLoading,
  InlineNotification,
  SkeletonPlaceholder,
} from '@carbon/react';
import { Settings } from '@carbon/react/icons';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { ProvideRootResource } from 'components/hydrate-atoms';
import { MountWhenVisible } from 'components/mount-when-visible';
import {
  RelatedItemsReadOnlyHint,
  RelatedItemsSection,
} from 'components/related-items/related-items-section';
import { SkeletonTextRows } from 'components/skeleton-text-rows';
import { WellEurStrip, WellStatusFields } from 'components/well-cards';
import type { WellsResponse } from 'types/api-responses';
import type { WellCompletionRow } from 'types/well-completions';
import { useAtomValue } from 'jotai';
import { useWellAnalysisHrefs, useWellIntersectionAnalysis } from './hooks';
import { selectedForecastEurAtom } from './selected-forecast-eur';
import { PerforationsTable } from './perforations-table';
import { PermitsTable } from './permits-table';
import { Stimulations } from './stimulations';
import { WellAnalysis } from './well-analysis';
import { WellDetailsContent } from './well-details';
import UnitDetails from './well-unit-details-table';
import { WellboresTable } from './wellbores-table';
import type { TraceColors } from 'components/wells-color-by';
import { WellMap } from './well-map';
import style from './well-page-content.module.scss';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';

// Highcharts modules compose into the one shared Highcharts instance, and
// highcharts-more's waterfall axis attaches its state to an axis when the axis is
// *created* — an axis built before the module was composed never gets it, and its next
// resize throws "Cannot read properties of undefined (reading 'stacks')". The Explorer's
// charts bring highcharts-more in with their lazy chunk, after this page's Well Analysis
// and DCA charts already exist, so it is composed here, before any of them. Composing
// is idempotent; the Explorer's own calls stay as they are.
highchartsMore(Highcharts);

// Lazily loaded + mounted only when scrolled into view. The Subsurface Explorer
// runs a large hook pipeline and fires several network requests on mount; keeping
// it off the initial wells-page load removes that work from the critical path.
// It is imported nowhere else, so this genuinely code-splits its unique modules.
const SubsurfaceExplorer = lazy(() =>
  import('./subsurface-explorer').then((m) => ({
    default: m.SubsurfaceExplorer,
  }))
);

/** Every section the Well page shows, in the order it shows them. */
export type WellPageSectionId =
  | 'well-details'
  | 'well-status'
  | 'eur'
  | 'unit-details'
  | 'map'
  | 'permits'
  | 'wellbores'
  | 'wellbore-completions'
  | 'stimulations'
  | 'subsurface-explorer'
  | 'well-analysis'
  | 'related-items';

export interface WellPageSection {
  id: WellPageSectionId;
  /** The section's name — its heading in either layout, and its Layout-settings label. */
  label: string;
  /**
   * True when the body renders its own heading, so a frame must not add a second one.
   * Three sections are Carbon accordions (which carry their own title and the collapse
   * the Well page has always had) and two are components that own a `Section` header.
   */
  ownsHeading?: boolean;
  body: ReactNode;
  /**
   * The body for the card layout, when it differs from `body`: the same content
   * without the section's own heading, since the card draws one (and its collapse). A
   * card renders `cardBody ?? body`.
   */
  cardBody?: ReactNode;
  /** Card-header controls beside the pin, when the section has any (the Explorer's gear). */
  actions?: ReactNode;
}

interface Options {
  well?: WellsResponse;
  uwi: string;
  versionId?: string;
  /**
   * Node the Subsurface Explorer portals its controls panel into, so a layout with a
   * settings rail can host them there instead of in a second sidebar inside the
   * section. Omitted by the grid, which keeps them inline as it always has.
   */
  subsurfaceFilterHost?: HTMLElement | null;
  /**
   * How the Subsurface Explorer arranges its views. The card layout asks for `quad` —
   * mock v13 shows all four at once, Map and 3D View above, 2D Profile and Gun Barrel
   * below, each expandable. Defaults to the tabs the grid has always shown, so the
   * grid and its callers (the packages evaluation-result panel) are unchanged.
   */
  subsurfaceLayout?: 'tabs' | 'quad';
  /**
   * Let the tabbed Subsurface Explorer grow to its content rather than fill a fixed
   * box — for the card layout, where the column scrolls and a card should not. See
   * SubsurfaceExplorer's `fitContent`. The grid keeps the box it has always had.
   */
  subsurfaceFitContent?: boolean;
  /**
   * Where the Subsurface Explorer's controls live: `inline` beside its views (the
   * grid, as always), or `drawer` — a push drawer inside the section that a gear in the
   * card header opens. The card layout asks for the drawer.
   */
  subsurfaceControls?: 'inline' | 'drawer';
  /**
   * Colour for the well's traces on the Map, resolved from the rail's colour-by rules.
   * Omitted by the grid, which has no rail and so no rules.
   */
  mapTraceColors?: TraceColors;
  /**
   * The well's completions, for the dates in Wellbore Completions; the caller reads them
   * with `useWellCompletions` from `lib/wells`. Without them the dates stay empty.
   */
  completions?: WellCompletionRow[];
  /** The failed completions read, or null. */
  completionsError?: Error | null;
}

/**
 * Picks and orders sections for one layout.
 *
 * Which sections a layout shows, and in what order, is a layout decision — the card
 * layout follows mock v13 while the grid keeps the order it has always had, and the
 * grid's callers can drop the Map. So each layout passes its own
 * manifest rather than the shared builder guessing. An id the builder does not produce
 * is skipped rather than rendering a hole.
 * @param {WellPageSection[]} sections Every section the builder produced.
 * @param {WellPageSectionId[]} order The ids this layout shows, in display order.
 * @return {WellPageSection[]} The chosen sections, ordered.
 */
export const pickSections = (
  sections: WellPageSection[],
  order: WellPageSectionId[]
): WellPageSection[] => {
  const byId = new Map(sections.map((section) => [section.id, section]));

  return order
    .map((id) => byId.get(id))
    .filter((section): section is WellPageSection => section !== undefined);
};

/**
 * Builds every Well page section once, for whichever layout renders them.
 *
 * The bodies and the state they read — the unit intersection, the unit selection, the
 * well-analysis hrefs — live here so the two layouts (the historical grid, and the
 * Well Workspace's card column) are a difference in framing only, and a section cannot
 * drift between them. Nothing is filtered here: a layout picks what it shows with
 * {@link pickSections}. Building a body it does not render costs nothing, since an
 * unrendered element fetches nothing.
 * @param {Options} options The well, and its UWI and version.
 * @return {WellPageSection[]} Every section, in the order the grid has always used.
 */
export const useWellPageSections = ({
  well,
  uwi,
  versionId,
  subsurfaceFilterHost,
  subsurfaceLayout = 'tabs',
  subsurfaceFitContent = false,
  subsurfaceControls = 'inline',
  mapTraceColors,
  completions,
  completionsError,
}: Options): WellPageSection[] => {
  const wellAnalysisHrefs = useWellAnalysisHrefs(well);
  // Production Details publishes the EUR for the Insights forecast it is
  // drawing; until one is selected the card falls back to the well's own.
  const selectedForecastEur = useAtomValue(selectedForecastEurAtom);
  // The Explorer's controls drawer, opened from the gear in its card header.
  const [explorerControlsOpen, setExplorerControlsOpen] = useState(false);

  // Use the well intersection analysis hook - fetches automatically when uwi/versionId changes
  const { data: intersectionData, loading: intersectionLoading } =
    useWellIntersectionAnalysis(uwi, versionId);

  // Derive initial selected units from intersection data
  // Case A: If assigned unit exists -> it's selected by default (always stays selected)
  // Case B: If no assigned unit -> first row is selected by default
  const defaultSelectedUnitIds = useMemo(() => {
    if (!intersectionData?.intersectingUnits?.length) return new Set<string>();

    const assignedUnit = intersectionData.intersectingUnits.find(
      (u) => u.assignedToUnit
    );

    if (assignedUnit) {
      // Case A: Assigned unit is selected by default
      return new Set([assignedUnit.name]);
    }

    // Case B: No assigned unit - select first row by default
    return new Set([intersectionData.intersectingUnits[0].name]);
  }, [intersectionData]);

  // Track user overrides for unit selection
  const [userSelectionOverrides, setUserSelectionOverrides] = useState<
    Map<string, boolean>
  >(new Map());

  // Reset user overrides when data changes
  const prevDataRef = useRef(intersectionData);
  useEffect(() => {
    if (intersectionData !== prevDataRef.current) {
      prevDataRef.current = intersectionData;
      setUserSelectionOverrides(new Map());
    }
  }, [intersectionData]);

  // Compute selected unit IDs by merging defaults with user overrides
  const selectedUnitIds = useMemo(() => {
    const result = new Set(defaultSelectedUnitIds);
    userSelectionOverrides.forEach((selected, unitId) => {
      if (selected) {
        result.add(unitId);
      } else {
        result.delete(unitId);
      }
    });
    return result;
  }, [defaultSelectedUnitIds, userSelectionOverrides]);

  // Check if there's an assigned unit
  const hasAssignedUnit = useMemo(() => {
    return (
      intersectionData?.intersectingUnits?.some((u) => u.assignedToUnit) ??
      false
    );
  }, [intersectionData]);

  // Handler for selection change from table
  // Both cases: Only one row can be selected at a time (excluding the always-selected assigned unit in Case A)
  const handleSelectionChange = useCallback(
    (unitId: string, selected: boolean) => {
      if (selected) {
        // Selecting a new row: Clear all other selections, then select this one
        const newOverrides = new Map<string, boolean>();
        // Deselect all previously selected units
        userSelectionOverrides.forEach((wasSelected, id) => {
          if (wasSelected && id !== unitId) {
            newOverrides.set(id, false);
          }
        });
        // Also deselect the default selected unit if it's different from the clicked one
        defaultSelectedUnitIds.forEach((id) => {
          if (id !== unitId && !hasAssignedUnit) {
            // For Case B: deselect default first row when selecting another
            newOverrides.set(id, false);
          }
        });
        // Select the new unit
        newOverrides.set(unitId, true);
        setUserSelectionOverrides(newOverrides);
      } else {
        // Deselecting: Simple toggle (only allowed for non-assigned units)
        setUserSelectionOverrides((prev) =>
          new Map(prev).set(unitId, selected)
        );
      }
    },
    [hasAssignedUnit, userSelectionOverrides, defaultSelectedUnitIds]
  );

  // The bodies the card layout shows without a section heading of their own — each is
  // built once and used both under the grid's accordion and as the card's body.
  const explorerContent = (
    <MountWhenVisible
      // With a rail host the Explorer had to mount at once so the host was not left
      // empty; without one it stays lazy, mounting when its card scrolls near.
      eager={Boolean(subsurfaceFilterHost)}
      placeholder={<InlineLoading description="Loading Subsurface Explorer" />}
    >
      <Suspense
        fallback={<InlineLoading description="Loading Subsurface Explorer" />}
      >
        <SubsurfaceExplorer
          uwi={uwi}
          wellName={well?.name}
          versionId={versionId}
          scopedTileUrls={well?.scoped?.tileUrls}
          intersectionData={intersectionData}
          selectedUnitIds={selectedUnitIds}
          filterHost={subsurfaceFilterHost}
          layout={subsurfaceLayout}
          fitContent={subsurfaceFitContent}
          controlsPlacement={subsurfaceControls}
          controlsOpen={explorerControlsOpen}
          // The quad's first cell. Built here rather than inside the quad because each
          // host brings its own map — the workspace scopes one to its selected well,
          // and this page's is the well's own. Only the quad has a cell for it, so the
          // tabbed layout is given nothing to mount.
          mapPanel={
            subsurfaceLayout === 'quad' && well ? (
              <ProvideRootResource>
                <WellMap
                  uwi={uwi}
                  versionId={versionId}
                  intersectionData={intersectionData}
                  selectedUnitIds={selectedUnitIds}
                />
              </ProvideRootResource>
            ) : undefined
          }
        />
      </Suspense>
    </MountWhenVisible>
  );
  const wellAnalysisContent = wellAnalysisHrefs ? (
    <WellAnalysis
      uwi={wellAnalysisHrefs.uwi}
      wellName={well?.name}
      forecastVersionHref={wellAnalysisHrefs.productionForecastsVersionsHref}
      recordVersionHref={wellAnalysisHrefs.productionRecordsVersionsHref}
      forecastHref={wellAnalysisHrefs.productionForecastsHref}
      recordHref={wellAnalysisHrefs.productionRecordsHref}
      dailyRecordHref={wellAnalysisHrefs.dailyProductionHref}
      dailyRecordVersionHref={wellAnalysisHrefs.dailyProductionVersionsHref}
      productionDataSources={wellAnalysisHrefs.productionDataSources}
    />
  ) : null;
  const relatedItems = (hideHeading: boolean) => {
    return well ? (
      <RelatedItemsSection
        host={{
          basePath: `/wells/${uwi}`,
          notifications: well.notifications,
        }}
        // Read-only on the Well page: linking, editing and removing happen inside a
        // workspace, where the change is staged and promoted rather than written
        // straight onto the well. Download stays — only writes are withdrawn.
        //
        // This is the ONLY thing enforcing that in R1. The write endpoints are not
        // gated: `/wells/:uwi/related_items` stays open because the pre-drill
        // workspace writes through it, and a workspace_id gate would break pre-drill
        // while protecting nothing (the Well Workspace has its own route). Removing
        // this prop re-opens the writes, it does not merely reveal a disabled path.
        readOnly
        hideHeading={hideHeading}
      />
    ) : (
      <SkeletonTextRows rows={5}></SkeletonTextRows>
    );
  };

  const sections: WellPageSection[] = [
    {
      id: 'well-details',
      label: 'Well Details',
      body: well ? (
        <WellDetailsContent well={well} />
      ) : (
        <div data-testid="well-details-skeleton">
          <SkeletonTextRows rows={3}></SkeletonTextRows>
        </div>
      ),
    },
    {
      // Mock v13 splits the status and its lifecycle dates out of Well Details into
      // their own card — the surface Epic 2 makes editable. The mock repeats those
      // fields on both cards and so does this; see WellStatusFields.
      id: 'well-status',
      label: 'Well Status',
      body: well ? (
        <WellStatusFields well={well} />
      ) : (
        <SkeletonTextRows rows={3}></SkeletonTextRows>
      ),
    },
    {
      // Where mock v13 puts it — the KPI strip sits high, above the tables. The
      // figures are the Insights forecast Production Details is drawing, and the
      // well's own promoted DCA forecast until one is selected. Both are already
      // in hand, so this needs no read of its own.
      id: 'eur',
      label: 'EUR',
      body: <WellEurStrip eur={selectedForecastEur ?? well?.eur} />,
    },
    {
      id: 'unit-details',
      label: 'Unit Details',
      body:
        well && !intersectionLoading ? (
          intersectionData && intersectionData.intersectingUnits.length > 0 ? (
            <UnitDetails
              data={intersectionData}
              selectedUnitIds={selectedUnitIds}
              onSelectionChange={handleSelectionChange}
            />
          ) : (
            <InlineNotification
              kind="warning"
              title="No units intersect with this well"
              lowContrast
              hideCloseButton
            />
          )
        ) : intersectionLoading ? (
          <DataTableSkeleton
            showHeader={false}
            showToolbar={false}
            columnCount={10}
            rowCount={3}
          />
        ) : null,
    },
    {
      id: 'map',
      label: 'Map',
      body: (
        <ProvideRootResource>
          {well ? (
            <WellMap
              uwi={uwi}
              versionId={versionId}
              intersectionData={intersectionData}
              selectedUnitIds={selectedUnitIds}
              traceColors={mapTraceColors}
            />
          ) : (
            <SkeletonPlaceholder
              data-testid="well-map-skeleton"
              className={style.mapLoad}
            />
          )}
        </ProvideRootResource>
      ),
    },
    {
      // The id stays `permits` for the same reason as `well-analysis` below.
      id: 'permits',
      label: 'Permit Details',
      body: (
        <PermitsTable
          uwi={uwi}
          resourceHref={well?.resources.queries.permitHeaders.href ?? ''}
        />
      ),
    },
    {
      id: 'wellbores',
      label: 'Wellbores',
      body: (
        <WellboresTable
          uwi={uwi}
          wellboresHref={well?.resources.queries.wellbores.href ?? ''}
          initialPerforationFactor={
            well?.estimatedPerforatedLateralLengthFactor ?? null
          }
          initialStickLateralFactor={well?.stickEstimatedLateralFactor}
          expandableWellborePoints
          /* Editing a geometry belongs to a Well Workspace, so the banner here says
             where to go rather than offering an edit this page cannot honour. */
          simplifiedGeometryBanner="readOnly"
        />
      ),
    },
    {
      id: 'wellbore-completions',
      label: 'Wellbore Completions',
      body: (
        <PerforationsTable
          uwi={uwi}
          resourceHref={well?.resources.queries.wellPerforations?.href ?? ''}
          completions={completions}
          completionsError={completionsError}
        />
      ),
    },
    {
      id: 'stimulations',
      label: 'Stimulations',
      body: (
        <Stimulations
          uwi={uwi}
          resourceHref="/queries/source_well_stimulations"
        />
      ),
    },
    {
      // An accordion in the grid, so it keeps the collapse the Well page has always
      // had; the card layout gets the same content under the card's own header.
      id: 'subsurface-explorer',
      label: 'Subsurface Explorer',
      ownsHeading: true,
      body: (
        <Accordion className={style.sectionAccordion}>
          <AccordionItem
            title={
              <span className={style.formationAndDepthTitle}>
                Subsurface Explorer
              </span>
            }
            open={true}
          >
            {explorerContent}
          </AccordionItem>
        </Accordion>
      ),
      cardBody: explorerContent,
      // The gear that opens the controls drawer inside the section. Only when the
      // controls live in the drawer; inline they need no opener.
      actions:
        subsurfaceControls === 'drawer' ? (
          <IconButton
            kind="ghost"
            size="md"
            align="bottom-right"
            label={
              explorerControlsOpen
                ? 'Hide Subsurface Explorer controls'
                : 'Show Subsurface Explorer controls'
            }
            onClick={() => setExplorerControlsOpen((open) => !open)}
          >
            <Settings />
          </IconButton>
        ) : undefined,
    },
    {
      // The id stays `well-analysis`: it keys the readers' saved layout settings,
      // so renaming it would turn their shown/hidden choice for this section back
      // to the default.
      id: 'well-analysis',
      label: 'Production Details',
      ownsHeading: true,
      body: (
        <Accordion className={style.sectionAccordion}>
          <AccordionItem title="Production Details" open={true}>
            {wellAnalysisContent}
          </AccordionItem>
        </Accordion>
      ),
      cardBody: wellAnalysisContent,
    },
    {
      // RelatedItemsSection renders its own "Related Items (N)" Section header in the
      // grid, with the read-only hint as its action; the card draws the heading itself,
      // so there the section renders no header and the hint sits in the card header,
      // left of the collapse and pin controls.
      id: 'related-items',
      label: 'Related Items',
      ownsHeading: true,
      body: relatedItems(false),
      cardBody: relatedItems(true),
      actions: <RelatedItemsReadOnlyHint />,
    },
  ];

  return sections;
};
